import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useConfig } from '../hooks/useConfig';

const Pricing = () => {
  const { t } = useTranslation();
  const { branchSignupUrl, companySignupUrl } = useConfig();
  const [openFaq, setOpenFaq] = useState(null);

  const plans = [
    {
      key: 'branch',
      popular: false,
      signupUrl: branchSignupUrl,
    },
    {
      key: 'company',
      popular: true,
      signupUrl: companySignupUrl,
    },
  ];

  const faqItems = t('pricing.faq.questions');

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#7876bb]/5 via-white to-[#7876bb]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('pricing.subtitle')}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 -mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {plans.map((plan) => {
              const planData = t(`pricing.${plan.key}`);
              const features = planData.features || [];
              
              return (
                <div
                  key={plan.key}
                  className={`relative bg-white rounded-2xl p-8 ${
                    plan.popular
                      ? 'border-2 border-[#7876bb] shadow-xl scale-105'
                      : 'border border-gray-200 shadow-lg'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-[#7876bb] text-white text-sm font-medium px-4 py-1 rounded-full">
                        Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {planData.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-6">
                      {planData.description}
                    </p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-2xl font-medium text-gray-600">€</span>
                      <span className="text-5xl font-bold text-gray-900 mx-1">
                        {planData.price}
                      </span>
                      <span className="text-gray-600">/{t('pricing.monthly')}</span>
                    </div>
                    <p className="text-sm text-[#7876bb] mt-2">
                      {t('pricing.freeTrial')}
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {Array.isArray(features) && features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-[#7876bb] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={plan.signupUrl}
                    className={`block w-full text-center py-3 px-6 rounded-xl font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-[#7876bb] text-white hover:bg-[#6665a8]'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {planData.cta}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            {t('pricing.faq.title')}
          </h2>
          <div className="space-y-4">
            {Array.isArray(faqItems) && faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-semibold text-gray-900">{item.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('cta.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-[#7876bb] rounded-xl hover:bg-[#6665a8] transition-colors shadow-lg shadow-[#7876bb]/30"
          >
            {t('cta.button')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
